import { useState } from 'react';
import dayjs from 'dayjs';
import { processOneDocumentForGetResource } from '../../../../utils/filesManagement/processDocumentForGetResource';

/**
 * Custom hook for managing configuration options related to creating and updating user profiles.
 * @function
 * @param {Object} options - The configuration options.
 * @param {Function} options.createFileFromJSON - Function to create a file from JSON data.
 * @param {Function} options.dispatchAPI - Function to dispatch API requests.
 * @param {Function} options.message - Function to display error messages.
 * @param {Function} options.setPhoto - Function to set the profile photo.
 * @param {Array} options.photo - Array containing the profile photo.
 * @param {Function} options.setDocumentsFileList - Function to set the documents file list.
 * @param {Object} options.documentsFileList - Object containing the documents file list.
 * @param {Function} options.setIsAnimals - Function to set the isAnimals state.
 * @returns {Object} - An object with configuration options.
 */
export const useCreateUpdateConfig = ({
  createFileFromJSON,
  dispatchAPI,
  message,
  setPhoto,
  photo,
  setDocumentsFileList,
  documentsFileList,
  setUser,
  removedFiles,
  setCountry,
  setIsAnimals
}) => {
  const [originalData, setOriginalData] = useState(null);

  const config = {
    onGetResource: {
      /**
       * Function to set fields when getting a resource.
       * @async
       * @function
       * @param {Object} data - The data received from the API.
       * @returns {Object} - Data with fields set as needed.
       */
      setFields: async (data) => {
        setOriginalData(data);

        if (data.photo) {
          try {
            const profilePhoto = await createFileFromJSON(
              data.photo,
              dispatchAPI,
              message
            );
            setPhoto([profilePhoto]);
          } catch (e) {
            message(e);
          }
        }

        if (data?.documents?.length) {
          const validDocuments = data.documents.filter((doc) => doc._id);

          const results = {};
          validDocuments.forEach((doc) => {
            const fileObj = {
              uid: String(doc._id?._id || doc._id),
              name: doc._id?.filename || doc.type,
              status: 'done',
              id: doc._id?._id || doc._id,
              url: `/pensioners/get-url-file/${doc._id?._id || doc._id}/TOKEN`
            };

            if (results[doc.type]) {
              results[doc.type].push(fileObj);
            } else {
              results[doc.type] = [fileObj];
            }
          });

          setDocumentsFileList((prev) => ({
            ...prev,
            ...results
          }));
        }

        if (data?.secondary_profile?.documents?.length) {
          try {
            const resolved = await Promise.all(
              (data.secondary_profile.documents || []).map((doc) =>
                processOneDocumentForGetResource(
                  doc,
                  createFileFromJSON,
                  dispatchAPI,
                  message
                )
              )
            );
            const mergedResult = resolved.reduce(
              (acc, obj) => ({ ...acc, [obj.type]: obj.file }),
              {}
            );

            const transformedObject = {};
            Object.keys(mergedResult).forEach((key) => {
              transformedObject[key] = [mergedResult[key]];
            });

            setDocumentsFileList((prev) => ({
              ...prev,
              secondary_profile: transformedObject
            }));
          } catch (error) {
            message(message(error));
          }
        }

        setCountry(data?.address?.country);
        setIsAnimals(data?.is_animals ?? false);

        return {
          ...data,
          birth_date: data.birth_date && dayjs(data.birth_date),
          secondary_profile: {
            ...data?.secondary_profile,
            birth_date:
              data?.secondary_profile?.birth_date &&
              dayjs(data?.secondary_profile?.birth_date)
          }
        };
      }
    },
    onCreateResource: {
      /**
       * Function to set the request body when creating a resource.
       * @function
       * @param {Object} data - The data to be sent in the request body.
       * @returns {FormData} - The FormData object with the request body.
       */
      setBody: (data) => {
        const formData = new FormData();
        try {
          if (photo.length) {
            formData.append('photo', photo[0]?.file || photo[0]);
          }

          if (Object.keys(documentsFileList).length) {
            Object.keys(documentsFileList).forEach((key) => {
              if (key === 'secondary_profile') {
                Object.keys(documentsFileList[key]).forEach((nestedKey) => {
                  const fileArray = documentsFileList[key][nestedKey];
                  if (Array.isArray(fileArray) && fileArray.length) {
                    const file = fileArray[0];
                    formData.append(nestedKey, file?.file || file);
                  }
                });
              }
              if (key === 'other_documents') {
                documentsFileList[key].forEach((file) => {
                  const fileToAppend = file?.file || file;
                  formData.append('other_documents', fileToAppend);
                });
              }
              if (key !== 'secondary_profile' && key !== 'other_documents') {
                const fileArray = documentsFileList[key];
                if (Array.isArray(fileArray) && fileArray.length) {
                  const file = fileArray[0];
                  const fileToAppend =
                    file instanceof File
                      ? file
                      : file?.originFileObj || file?.file || file;
                  formData.append(key, fileToAppend);
                }
              }
            });
          }
        } catch (e) {
          if (e.response) message(e.response.status);
        }

        const newData = { ...data, removedFiles };
        formData.append('values', JSON.stringify(newData));

        return formData;
      }
    },
    onUpdateResource: {
      /**
       * Function to set the request body when updating a resource.
       * @function
       * @param {Object} data - The data to be sent in the request body.
       * @returns {FormData} - The FormData object with the request body.
       */
      setBody: (data) => {
        const formData = new FormData();

        const mergedData = {
          ...originalData,
          ...data,
          secondary_profile: {
            ...(originalData?.secondary_profile || {}),
            ...(data?.secondary_profile || {})
          }
        };

        try {
          if (photo.length) {
            formData.append('photo', photo[0]?.file || photo[0]);
          }

          if (Object.keys(documentsFileList).length) {
            Object.keys(documentsFileList).forEach((key) => {
              if (key === 'secondary_profile') {
                Object.keys(documentsFileList[key]).forEach((nestedKey) => {
                  const fileArray = documentsFileList[key][nestedKey];
                  if (Array.isArray(fileArray) && fileArray.length) {
                    const file = fileArray[0];
                    formData.append(nestedKey, file?.file || file);
                  }
                });
              }
              if (key === 'other_documents') {
                documentsFileList[key].forEach((file) => {
                  const fileToAppend = file?.file || file;
                  formData.append('other_documents', fileToAppend);
                });
              }
              if (key !== 'secondary_profile' && key !== 'other_documents') {
                const fileArray = documentsFileList[key];
                if (Array.isArray(fileArray) && fileArray.length) {
                  const file = fileArray[0];
                  if (file?.status === 'done' && !file?.originFileObj) {
                    return;
                  }
                  const fileToAppend =
                    file instanceof File
                      ? file
                      : file?.originFileObj || file?.file || file;
                  formData.append(key, fileToAppend);
                }
              }
            });
          }
        } catch (e) {
          if (e.response) message(e.response.status);
        }

        setUser((prevData) => ({ ...prevData, ...mergedData }));

        const newData = { ...mergedData, removedFiles };
        formData.append('values', JSON.stringify(newData));

        return formData;
      }
    }
  };

  return { config };
};
